import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ProgressBar } from '../ui/ProgressBar';
import { universalQuestions } from '../../data/universalQuestions';
import { QuillWritingLoader } from '../ui/QuillWritingLoader';
import { ThemeId } from '../../data/quizData';
interface QuizPhaseProps {
  themeId: ThemeId;
  onComplete: (answers: Record<string, string>) => void;
}
export const QuizPhase = ({
  onComplete
}: QuizPhaseProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [textValue, setTextValue] = useState('');
  const [mode, setMode] = useState<'question' | 'loading'>('question');
  const currentQuestion = universalQuestions[currentIndex];
  const total = universalQuestions.length;
  // Progress 30% → 80% across all 15 questions
  const progressBase = 30;
  const progressRange = 50;
  const progress = progressBase + currentIndex / total * progressRange;
  // After answer is recorded, show loading screen then advance
  const recordAnswer = (value: string) => {
    const newAnswers = {
      ...answers,
      [currentQuestion.id]: value
    };
    setAnswers(newAnswers);
    setMode('loading');
    setTimeout(() => {
      if (currentIndex < total - 1) {
        setCurrentIndex(currentIndex + 1);
        setTextValue('');
        setMode('question');
      } else {
        onComplete(newAnswers);
      }
    }, currentQuestion.loadingDuration);
  };
  const handleTextSubmit = () => {
    const v = textValue.trim();
    if (v.length < 3) return;
    recordAnswer(v);
  };
  // Render the loading screen
  if (mode === 'loading') {
    return <AnimatePresence mode="wait" data-id="element-224">
        <QuillWritingLoader key={`loader-${currentQuestion.id}`} phrase={currentQuestion.loadingPhrase} data-id="element-225" />
      </AnimatePresence>;
  }
  return <div className="w-full min-h-screen flex flex-col" style={{
    backgroundColor: '#0A0A0A'
  }} data-id="element-226">
      {/* Top progress */}
      <div className="w-full max-w-3xl mx-auto px-4 pt-8" data-id="element-227">
        <ProgressBar progress={progress} label={`Pregunta ${currentIndex + 1} de ${total}`} data-id="element-228" />
      </div>

      <div className="flex-1 w-full max-w-3xl mx-auto px-4 py-8 flex flex-col justify-center" data-id="element-229">
        <AnimatePresence mode="wait" data-id="element-230">
          <motion.div key={currentQuestion.id} initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -30
        }} transition={{
          duration: 0.5
        }} className="w-full" data-id="element-231">
            {currentQuestion.kind === 'text' ? <TextQuestion title={currentQuestion.title} subtitle={currentQuestion.subtitle} placeholder={currentQuestion.placeholder} privacyNote={currentQuestion.privacyNote} value={textValue} onChange={setTextValue} onSubmit={handleTextSubmit} data-id="element-232" /> : <MultipleChoiceQuestion title={currentQuestion.title} options={currentQuestion.options} onSelect={recordAnswer} data-id="element-233" />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
};
/* ============================================================
   TEXT QUESTION (Q1)
============================================================ */
interface TextQuestionProps {
  title: string;
  subtitle?: string;
  placeholder: string;
  privacyNote: string;
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
}
const TextQuestion = ({
  title,
  subtitle,
  placeholder,
  privacyNote,
  value,
  onChange,
  onSubmit
}: TextQuestionProps) => {
  const minChars = 3;
  const canSubmit = value.trim().length >= minChars;
  return <div className="text-center" data-id="element-234">
      <h2 className="font-cormorant mb-4 leading-tight" style={{
      color: '#FFFFFF',
      fontSize: 'clamp(22px, 3.5vw, 30px)',
      fontWeight: 600
    }} data-id="element-235">
        {title}
      </h2>
      {subtitle && <p className="font-sans mx-auto max-w-2xl mb-10" style={{
      color: '#E0D4BC',
      fontSize: '15.5px',
      lineHeight: 1.6,
      fontWeight: 400
    }} data-id="element-236">
          {subtitle}
        </p>}

      <div className="relative max-w-2xl mx-auto" data-id="element-237">
        <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={6} className="w-full bg-black/60 border rounded-sm px-5 py-4 font-sans focus:outline-none transition-colors resize-none" style={{
        borderColor: 'rgba(212,175,55,0.35)',
        color: '#F5F0E8',
        fontSize: '15.5px',
        lineHeight: 1.6
      }} autoFocus data-id="element-238" />
      </div>

      <p className="mt-4 font-sans text-center max-w-xl mx-auto" style={{
      color: '#b8923d',
      fontSize: '12.5px',
      letterSpacing: '0.02em',
      lineHeight: 1.5
    }} data-id="element-239">
        {privacyNote}
      </p>

      <button onClick={onSubmit} disabled={!canSubmit} className="mt-8 font-cinzel uppercase tracking-widest transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed" style={{
      backgroundColor: '#5c1a1b',
      color: '#F5F0E8',
      fontSize: '13.5px',
      padding: '14px 36px',
      border: '1px solid rgba(212,175,55,0.5)',
      letterSpacing: '0.18em',
      fontWeight: 600,
      borderRadius: '2px',
      boxShadow: canSubmit ? '0 0 24px rgba(212,175,55,0.18)' : 'none'
    }} data-id="element-240">
        Continuar
      </button>
    </div>;
};
/* ============================================================
   MULTIPLE CHOICE QUESTION (Q2–Q15)
============================================================ */
interface MultipleChoiceQuestionProps {
  title: string;
  options: {
    label: string;
    value: string;
  }[];
  onSelect: (value: string) => void;
}
const MultipleChoiceQuestion = ({
  title,
  options,
  onSelect
}: MultipleChoiceQuestionProps) => {
  const [selected, setSelected] = useState<string | null>(null);
  const handle = (value: string) => {
    if (selected) return;
    setSelected(value);
    // brief visual feedback delay before triggering loader
    setTimeout(() => onSelect(value), 350);
  };
  return <div data-id="element-241">
      <h2 className="font-cormorant text-center mb-10 leading-tight" style={{
      color: '#FFFFFF',
      fontSize: 'clamp(20px, 3.2vw, 28px)',
      fontWeight: 600,
      lineHeight: 1.35,
      maxWidth: '720px',
      marginLeft: 'auto',
      marginRight: 'auto'
    }} data-id="element-242">
        {title}
      </h2>

      <div className="space-y-3 max-w-2xl mx-auto" data-id="element-243">
        {options.map((option, idx) => {
        const isSelected = selected === option.value;
        return <motion.button key={option.value} onClick={() => handle(option.value)} className="w-full text-left transition-all duration-300 disabled:cursor-default" disabled={selected !== null && !isSelected} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: idx * 0.07,
          duration: 0.35
        }} whileHover={{
          scale: selected ? 1 : 1.01
        }} whileTap={{
          scale: 0.99
        }} style={{
          padding: '16px 20px',
          borderRadius: '3px',
          backgroundColor: isSelected ? '#8a6e1f' : 'rgba(0,0,0,0.6)',
          color: isSelected ? '#0A0A0A' : '#F5F0E8',
          border: isSelected ? '1px solid #D4AF37' : '1px solid rgba(212,175,55,0.3)',
          boxShadow: isSelected ? '0 0 24px rgba(212,175,55,0.45), inset 0 0 18px rgba(212,175,55,0.15)' : 'none',
          fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
          fontSize: '15px',
          lineHeight: 1.5,
          fontWeight: 500,
          opacity: selected !== null && !isSelected ? 0.4 : 1
        }} data-id="element-244">
              {option.label}
            </motion.button>;
      })}
      </div>
    </div>;
};